import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Breadcrumb } from '../Breadcrumb';
import { PageTitle } from '../PageTitle';
import { ContactSection } from '../features/ContactSection';


const PAGETITLE_JP = '開発';
const PAGETITLE_EN = 'Development';

/**
 * ServiceDevelopmentPage Component - Service Development Page
 * 
 * Features:
 * - Company development overview
 * - Technical expertise and skills
 * - Industry solutions and domains
 * - Other service solutions
 * - Mobile responsive design
 * - Auto-scroll to top on page load
 */

const ServiceDevelopmentPageContainer = styled.div.attrs({className: 'page-container'})`
  background: #F9FAFA;
  min-height: 100vh;
  padding: 0 0 207px;

  @media (max-width: 768px) {
      padding: 0 20px 95px;
`;



const CompanyOverviewSection = styled.section.attrs({className: 'company-overview-section'})`
  padding-top: 127px;
  margin: 0 auto;
  max-width: var(--contents-maxwidth-pc);

  @media (max-width: 768px) {
    padding-top: 60px;
    margin: 0;
  }
`;

const CompanyOverviewTitle = styled.h2.attrs({className: 'company-overview-title'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 36px;
  line-height: 1.5;
  letter-spacing: 0.05em;
  color: #333333;
  margin: 0 0 90px 0;
  text-align: left;

  @media (max-width: 768px) {
    font-size: 28px;
    margin: 0 0 60px 0;
  }
`;

const CompanyOverviewContent = styled.div.attrs({className: 'company-overview-content'})`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 90px;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 60px;
  }
`;

const CompanyOverviewText = styled.div.attrs({className: 'company-overview-text'})`
  display: flex;
  flex-direction: column;
  gap: 50px;
  align-items: flex-end;

  @media (max-width: 768px) {
    gap: 30px;
    align-items: flex-start;
  }
`;

const CompanyOverviewQuote = styled.div.attrs({className: 'company-overview-quote'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 700;
  font-size: 24px;
  line-height: 1.75;
  letter-spacing: 0.05em;
  color: #333333;
  align-self: flex-start;

  @media (max-width: 768px) {
    font-size: 20px;
    // padding-left: 20px;
    // padding-right: 20px;
  }
`;

const CompanyOverviewDescription = styled.div.attrs({className: 'company-overview-description'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 1.75;
  color: #333333;
  max-width: 610px;
  text-align: justify;

  p {
    margin: 0;
  }

  p:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 1.6;
    max-width: none;
    // padding-left: 20px;
    // padding-right: 20px;
  }
`;

const CompanyOverviewImage = styled.picture.attrs({className: 'company-overview-image'})`
  display: block;
  aspect-ratio: 580/336;
  
  @media (max-width: 768px) {
    aspect-ratio: 1/1;
    border-radius: 0;
    margin-left: -20px;
    margin-right: -20px;
    width: calc(100% + 40px);
  }

  img {
    width: 100%;
  }
`;

const ProfessionalTeamSection = styled.section.attrs({className: 'professional-team-section'})`
  padding-top: 151px;
  margin: 0 auto;
  max-width: var(--contents-maxwidth-pc);
  @media (max-width: 768px) {
    padding-top: 60px;
    margin: 0;
  }
  
  .company-overview-image {
    @media (max-width: 768px) {
      order: 2;
    }
  }
`;

const ProfessionalTeamContent = styled.div.attrs({className: 'professional-team-content'})`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 90px;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;



const ProfessionalTeamText = styled.div.attrs({className: 'professional-team-text'})`
  display: flex;
  flex-direction: column;
  gap: 50px;
  align-items: flex-start;

  @media (max-width: 768px) {
    gap: 30px;
  }
`;

const ProfessionalTeamTitle = styled.h3.attrs({className: 'professional-team-title'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 700;
  font-size: 24px;
  line-height: 1.75;
  letter-spacing: 0.05em;
  color: #333333;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 20px;
    // padding-left: 20px;
    // padding-right: 20px;
  }
`;

const ProfessionalTeamDescription = styled.div.attrs({className: 'professional-team-description'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 1.75;
  color: #333333;
  text-align: justify;

  p {
    margin: 0;
  }

  p:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 1.6;
    // padding-left: 20px;
    // padding-right: 20px;

    p {
      margin: 0 0 15px 0;
    }
  }
`;

const IndustrySolutionsSection = styled.section.attrs({className: 'industry-solutions-section'})`
  padding-top: 151px;
  margin: 0 auto;
  max-width: var(--contents-maxwidth-pc);
  @media (max-width: 768px) {
    padding-top: 120px;
    margin-left: -20px;
    margin-right: -20px;
    width: calc(100% + 40px);
  }
`;

const IndustrySolutionsContainer = styled.div.attrs({className: 'industry-solutions-container'})`
  background: #FFFFFF;
  border-radius: 20px;
  padding: 80px;

  @media (max-width: 768px) {
    padding: 120px 20px;
    border-radius: 0;
  }
`;

const IndustrySolutionsTitle = styled.h2.attrs({className: 'industry-solutions-title'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 36px;
  line-height: 1.5;
  letter-spacing: 0.05em;
  color: #FFFFFF;
  margin: 0 0 60px 0;
  text-align: left;

  @media (max-width: 768px) {
    font-size: 28px;
    margin: 0 0 80px 0;
  }
`;

const IndustrySolutionsGrid = styled.div.attrs({className: 'industry-solutions-grid'})`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 60px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const IndustrySolutionItem = styled.div.attrs({className: 'industry-solutions-item'})`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const IndustrySolutionDivider = styled.hr.attrs({className: 'industry-solutions-divider'})`
  border: none;
  height: 1px;
  background: #FFFFFF;
  margin: 0;
`;

const IndustrySolutionTitle = styled.h3.attrs({className: 'industry-solutions-title'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 700;
  font-size: 24px;
  line-height: 1.75;
  letter-spacing: 0.05em;
  color: #FFFFFF;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const IndustrySolutionDescription = styled.div.attrs({className: 'industry-solutions-description'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 1.75;
  color: #FFFFFF;

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 1.6;
  }
`;

const TechnicalExpertiseSection = styled.section.attrs({className: 'technical-expertise-section'})`
  padding-top: 78px;
  margin: 0 auto;
  max-width: var(--contents-maxwidth-pc);

  @media (max-width: 768px) {
    padding-top: 0;
    margin: 0;
  }
`;

const TechnicalExpertiseContainer = styled.div.attrs({className: 'technical-expertise-container'})`
  background: #FFFFFF;
  border-radius: 20px;
  padding: 100px 87px 116px;

  @media (max-width: 768px) {
    padding: 120px 20px;
    border-radius: 0;
    margin-left: -20px;
    margin-right: -20px;
    width: calc(100% + 40px);
  }
`;

const TechnicalExpertiseSectionTitle = styled.h2.attrs({className: 'technical-expertise-section-title'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 36px;
  line-height: 1.5;
  letter-spacing: 0.05em;
  color: #0F3F8E;
  margin: 0 0 80px 0;
  text-align: left;

  @media (max-width: 768px) {
    font-size: 28px;
    margin: 0 0 80px 0;
  }
`;

const TechnicalExpertiseGrid = styled.div.attrs({className: 'technical-expertise-grid'})`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 72px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TechnicalExpertiseItem = styled.div.attrs({className: 'technical-expertise-item'})`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const TechnicalExpertiseItemTitle = styled.h3.attrs({className: 'technical-expertise-item-title'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 700;
  font-size: 24px;
  line-height: 1.75;
  letter-spacing: 0.05em;
  color: #0F3F8E;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const TechnicalExpertiseDivider = styled.hr.attrs({className: 'industry-solutions-divider'})`
  border: none;
  height: 1px;
  background: #0F3F8E;
  margin: 0;
`;

const TechnicalExpertiseTags = styled.div.attrs({className: 'technical-expertise-tags'})`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

const TechnicalExpertiseTag = styled.span.attrs({className: 'technical-expertise-tag'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 1.75;
  color: #333333;
  background: #FFFFFF;
  border: 1px solid #333333;
  border-radius: 5px;
  padding: 4px 40px;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 14px;
    padding: 4px 20px;
    min-width: 120px;
  }
`;

const OtherSolutionsSection = styled.section.attrs({className: 'other-solutions-section'})`
  margin: 0 auto;
  max-width: var(--contents-maxwidth-pc);
  @media (max-width: 768px) {
    margin: 0;
  }
`;

const OtherSolutionsTitle = styled.h2.attrs({className: 'other-solutions-title'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 36px;
  line-height: 1.5;
  letter-spacing: 0.05em;
  color: #0F3F8E;
  margin: 0 0 80px 0;
  text-align: left;

  @media (max-width: 768px) {
    font-size: 28px;
    margin: 0 0 40px 0;
  }
`;

const OtherSolutionsGrid = styled.div.attrs({className: 'other-solutions-grid'})`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const OtherSolutionCard = styled(Link).attrs({className: 'other-solution-card'})`
  text-decoration: none;
  background: #FFFFFF;
  border-radius: 20px;
  padding: 50px;
  display: flex;
  flex-direction: column;
  gap: 30px;

  @media (max-width: 768px) {
    padding: 30px 20px;
    gap: 20px;
  }
`;

const OtherSolutionTitle = styled.h3.attrs({className: 'other-solution-title'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 700;
  font-size: 24px;
  line-height: 1.75;
  letter-spacing: 0.05em;
  color: #333333;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const OtherSolutionDescription = styled.p.attrs({className: 'other-solution-description'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 1.75;
  color: #333333;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 1.6;
  }
`;

const OtherSolutionButton = styled.div.attrs({className: 'other-solution-button'})`
  display: flex;
  align-items: center;
  gap: 20px;
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 700;
  font-size: 13px;
  line-height: 1.38;
  letter-spacing: 0.05em;
  color: #333333;
  text-decoration: none;
  transition: color 0.3s ease;
  align-self: flex-end;

  &:hover {
    color: #0F3F8E;
  }
`;



const ServiceDevelopmentPage: React.FC = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <ServiceDevelopmentPageContainer>


      {/* Breadcrumb Navigation */}
      <Breadcrumb 
        items={[
          { label: "事業紹介", path: "/service/" }, // 2階層目（リンクあり）
          { label: PAGETITLE_JP }           // 3階層目（現在のページなのでリンクなし）
        ]} 
      />


      {/* Page Title Section */}
      <PageTitle title={ PAGETITLE_JP } subtitle={ PAGETITLE_EN } />

      {/* Company Overview Section */}
      <CompanyOverviewSection>
        <CompanyOverviewTitle>リブゲートの開発について</CompanyOverviewTitle>
        <CompanyOverviewContent>
          <CompanyOverviewText>
            <CompanyOverviewQuote>
              お客様のビジネスに寄り添い、<br />
              最適なシステムソリューションを提供いたします。
            </CompanyOverviewQuote>
            <CompanyOverviewDescription>
              <p>
                株式会社リブゲートは、創業以来、大規模業務システムの開発・運用において豊富な実績を有しております。
              </p>
              <p>
                弊社は、お客様の視点に立ち、エンドユーザー様の利便性を追求したシステム開発を最重要視しております。そのため、プロジェクトにおいては、エンドユーザー様との直接対話を重視し、綿密なヒアリングを重ねることで、お客様のニーズを的確に捉えたシステム構築を実現いたします。
              </p>
              <p>
                単なるシステム開発に留まらず、開発後の運用・保守、機能追加、システム刷新まで、長期にわたるサポート体制を確立し、お客様のビジネス成長に貢献いたします。
              </p>
            </CompanyOverviewDescription>
          </CompanyOverviewText>
          <CompanyOverviewImage>
            <source 
                  media="(min-width: 769px)" 
                  srcSet="/assets/images/service/develop/develop_01_pc.jpg" 
                />
              <img src="/assets/images/service/develop/develop_01_sp.jpg" alt="開発イメージ" />
          </CompanyOverviewImage>
        </CompanyOverviewContent>
      </CompanyOverviewSection>

      {/* Professional Team Section */}
      <ProfessionalTeamSection>
        <ProfessionalTeamContent>
          <CompanyOverviewImage>
            <source 
                  media="(min-width: 769px)" 
                  srcSet="/assets/images/service/develop/develop_02_pc.jpg" 
                />
              <img src="/assets/images/service/develop/develop_02_sp.jpg" alt="開発イメージ" />
          </CompanyOverviewImage>
          <ProfessionalTeamText>
            <ProfessionalTeamTitle>卓越した技術力を有するプロフェッショナル集団</ProfessionalTeamTitle>
            <ProfessionalTeamDescription>
              <p>
                20年以上の豊富な経験を有するコンサルタントやPMクラスのエンジニアに加え、大人数のチームを統括するリーダー、各工程を管理するサブリーダー、設計、製造、試験、リリースを担う専門性の高いメンバーなど、多様な人材を有しております。
              </p>
              <p>
                また、新卒エンジニアの育成にも注力しており、入社前研修を含む9ヶ月間の長期研修を実施しております。研修では、JAVAやデータベースといった基礎知識に加え、開発業務の進め方や配属先のニーズに合わせた個別カリキュラムを提供し、即戦力となるエンジニアを育成しております。
              </p>
              <p>
                これにより、お客様に安定した高品質の技術サービスをご提供いたします。
              </p>
            </ProfessionalTeamDescription>
          </ProfessionalTeamText>
        </ProfessionalTeamContent>
      </ProfessionalTeamSection>

      {/* Industry Solutions Section */}
      <IndustrySolutionsSection>
        <IndustrySolutionsContainer style={{ background: '#0F3F8E' }}>
          <IndustrySolutionsTitle>領域・分野</IndustrySolutionsTitle>
          <IndustrySolutionsGrid>
            <IndustrySolutionItem>
              <IndustrySolutionTitle>通信企業向け</IndustrySolutionTitle>
              <IndustrySolutionDivider/>
              <IndustrySolutionDescription>
                ｜ 設備系工事管理システム開発および運用<br />
                ｜ 店頭受付システム開発および運用<br />
                ｜ 商談管理システム開発および運用
              </IndustrySolutionDescription>
            </IndustrySolutionItem>
            <IndustrySolutionItem>
              <IndustrySolutionTitle>官公庁向け</IndustrySolutionTitle>
              <IndustrySolutionDivider/>
              <IndustrySolutionDescription>
                ｜ デジタル申請プラットフォーム開発
              </IndustrySolutionDescription>
            </IndustrySolutionItem>
            <IndustrySolutionItem>
              <IndustrySolutionTitle>自動車メーカー向け</IndustrySolutionTitle>
              <IndustrySolutionDivider/>
              <IndustrySolutionDescription>
                ｜ 販社向け営業管理システム導入
              </IndustrySolutionDescription>
            </IndustrySolutionItem>
            <IndustrySolutionItem>
              <IndustrySolutionTitle>自治体向け</IndustrySolutionTitle>
              <IndustrySolutionDivider/>
              <IndustrySolutionDescription>
                ｜ 自治体専用グループウェア開発運用
              </IndustrySolutionDescription>
            </IndustrySolutionItem>
            <IndustrySolutionItem>
              <IndustrySolutionTitle>金融機関向け</IndustrySolutionTitle>
              <IndustrySolutionDivider/>
              <IndustrySolutionDescription>
                ｜ 地方銀行向けCRMシステム導入<br />
                ｜ メガバンク向けマーケティングデータ分析
              </IndustrySolutionDescription>
            </IndustrySolutionItem>
          </IndustrySolutionsGrid>
        </IndustrySolutionsContainer>
      </IndustrySolutionsSection>

      {/* Technical Expertise Section */}
      <TechnicalExpertiseSection>
        <TechnicalExpertiseContainer>
          <TechnicalExpertiseSectionTitle>技術</TechnicalExpertiseSectionTitle>
          <TechnicalExpertiseGrid>
            <TechnicalExpertiseItem>
              <TechnicalExpertiseItemTitle>フロント開発</TechnicalExpertiseItemTitle>
              <TechnicalExpertiseDivider/>
              <TechnicalExpertiseTags>
                <TechnicalExpertiseTag>Vue.js</TechnicalExpertiseTag>
                <TechnicalExpertiseTag>React</TechnicalExpertiseTag>
                <TechnicalExpertiseTag>Angular</TechnicalExpertiseTag>
                <TechnicalExpertiseTag>PHP</TechnicalExpertiseTag>
                <TechnicalExpertiseTag>TypeScript</TechnicalExpertiseTag>
              </TechnicalExpertiseTags>
            </TechnicalExpertiseItem>
            <TechnicalExpertiseItem>
              <TechnicalExpertiseItemTitle>バックエンド開発</TechnicalExpertiseItemTitle>
              <TechnicalExpertiseDivider/>
              <TechnicalExpertiseTags>
                <TechnicalExpertiseTag>Java</TechnicalExpertiseTag>
                <TechnicalExpertiseTag>Python</TechnicalExpertiseTag>
                <TechnicalExpertiseTag>C#</TechnicalExpertiseTag>
                <TechnicalExpertiseTag>PHP</TechnicalExpertiseTag>
                <TechnicalExpertiseTag>Go</TechnicalExpertiseTag>
              </TechnicalExpertiseTags>
            </TechnicalExpertiseItem>
            <TechnicalExpertiseItem>
              <TechnicalExpertiseItemTitle>クラウド関連</TechnicalExpertiseItemTitle>
              <TechnicalExpertiseDivider/>
              <TechnicalExpertiseTags>
                <TechnicalExpertiseTag>Salesforce</TechnicalExpertiseTag>
                <TechnicalExpertiseTag>Heroku</TechnicalExpertiseTag>
                <TechnicalExpertiseTag>Dynamics</TechnicalExpertiseTag>
                <TechnicalExpertiseTag>AWS</TechnicalExpertiseTag>
                <TechnicalExpertiseTag>Azure</TechnicalExpertiseTag>
                <TechnicalExpertiseTag>GCP</TechnicalExpertiseTag>
              </TechnicalExpertiseTags>
            </TechnicalExpertiseItem>
          </TechnicalExpertiseGrid>
        </TechnicalExpertiseContainer>
      </TechnicalExpertiseSection>

      {/* Contact Section */}
      <ContactSection />

      {/* Other Solutions Section */}
      <OtherSolutionsSection>
        <OtherSolutionsTitle>他のソリューションを見る</OtherSolutionsTitle>
        <OtherSolutionsGrid>
          <OtherSolutionCard to="/service/creative/">
            <OtherSolutionTitle>クリエイティブ</OtherSolutionTitle>
            <OtherSolutionDescription>
              Webサイトやランディングページ制作からブランディング戦略立案まで、お客様のデジタルプレゼンスを最大化します。
            </OtherSolutionDescription>
            <OtherSolutionButton>
              詳しく見る
              <img src="/images/arrow-right.svg" alt="arrow right" />
            </OtherSolutionButton>
          </OtherSolutionCard >
          <OtherSolutionCard to="/service/product/">
            <OtherSolutionTitle>プロダクト</OtherSolutionTitle>
            <OtherSolutionDescription>
              デジタル領域の知見とものづくりへの情熱を融合し、バッグブランド「FADEN®︎」を展開しています。
            </OtherSolutionDescription>
            <OtherSolutionButton>
              詳しく見る
              <img src="/images/arrow-right.svg" alt="arrow right" />
            </OtherSolutionButton>
          </OtherSolutionCard>
        </OtherSolutionsGrid>
      </OtherSolutionsSection>
    </ServiceDevelopmentPageContainer>
  );
};

export default ServiceDevelopmentPage;
